import React from "react";
import { ExternalLink, Scale, FileText, Landmark, ShieldQuestion, ShieldCheck, ShieldAlert } from "lucide-react";

/**
 * SourceCard — renders the structured citations returned by the legal
 * retrieval orchestrator (CourtListener / Open States / GovInfo) as a
 * compact, link-anchored card under each assistant reply.
 *
 * The chip's [S#] tag matches the tag the LLM cited inline in the answer
 * markdown — so a user can scan the answer, see [S3], and find the same
 * tag in the card below.
 */

const KIND_META = {
    case: { Icon: Scale, label: "Case law", color: "border-amber-200 bg-amber-50/40 text-amber-900" },
    bill: { Icon: Landmark, label: "State legislation", color: "border-emerald-200 bg-emerald-50/40 text-emerald-900" },
    federal: { Icon: FileText, label: "Federal record", color: "border-sky-200 bg-sky-50/40 text-sky-900" },
};

const CONFIDENCE_META = {
    source_grounded: {
        Icon: ShieldCheck,
        label: "Answer cites indexed sources",
        tone: "border-emerald-200 bg-emerald-50/50 text-emerald-900",
    },
    general_info: {
        Icon: ShieldAlert,
        label: "General legal information (not directly cited)",
        tone: "border-amber-200 bg-amber-50/50 text-amber-900",
    },
    unable_to_verify: {
        Icon: ShieldQuestion,
        label: "Couldn't verify in indexed sources — treat as research lead",
        tone: "border-orange-200 bg-orange-50/50 text-orange-900",
    },
};

const STRATEGY_META = {
    expanded: { label: "concept match", tone: "text-violet-700" },
    topic_cluster: { label: "topic match", tone: "text-violet-700" },
    exact: null,
};

export default function SourceCard({ sources = [], providersSucceeded = [], confidenceSignal, testid = "source-card" }) {
    // Render nothing if we have neither sources nor a confidence pill to show.
    if ((!sources || sources.length === 0) && !confidenceSignal) return null;

    const meta = CONFIDENCE_META[confidenceSignal] || (confidenceSignal ? {
        // Defensive fallback so a future backend signal (e.g., 'partial_match')
        // still renders something rather than silently dropping the pill.
        Icon: ShieldAlert,
        label: confidenceSignal.replace(/_/g, " "),
        tone: "border-border bg-muted text-muted-foreground",
    } : null);
    const ConfIcon = meta?.Icon;

    return (
        <div className="mt-3 pt-3 border-t border-border/60" data-testid={testid}>
            {/* Confidence pill — sets expectations BEFORE the user reads the citations. */}
            {meta && (
                <div className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${meta.tone} mb-2`}
                     data-testid={`${testid}-confidence-${confidenceSignal}`}>
                    {ConfIcon ? <ConfIcon className="h-3 w-3" /> : null}
                    {meta.label}
                </div>
            )}

            {sources.length > 0 && (
                <>
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2" data-testid={`${testid}-heading`}>
                        Sources checked{providersSucceeded.length ? ` · ${providersSucceeded.join(" · ")}` : ""}
                    </div>
                    <ul className="space-y-1.5">
                        {sources.map((s) => {
                            const m = KIND_META[s.kind] || KIND_META.case;
                            const Icon = m.Icon;
                            const strategy = STRATEGY_META[s.match_strategy];
                            return (
                                <li key={s.tag} data-testid={`${testid}-item-${s.tag}`}>
                                    <a
                                        href={s.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`group flex items-start gap-2 text-xs rounded-md border px-2.5 py-2 hover:shadow-sm transition-shadow ${m.color}`}
                                    >
                                        <span className="font-mono font-bold text-[10px] mt-0.5 flex-shrink-0">[{s.tag}]</span>
                                        <Icon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                        <span className="flex-1 min-w-0">
                                            <span className="block font-semibold leading-snug truncate group-hover:underline">{s.title}</span>
                                            <span className="block text-[10px] opacity-80 mt-0.5">
                                                {s.source}{s.jurisdiction ? ` · ${s.jurisdiction}` : ""}{s.citation ? ` · ${s.citation}` : ""}
                                                {strategy ? <span className={`ml-1.5 font-semibold ${strategy.tone}`} data-testid={`${testid}-strategy-${s.tag}`}>· {strategy.label}</span> : null}
                                            </span>
                                        </span>
                                        <ExternalLink className="h-3 w-3 mt-1 flex-shrink-0 opacity-60 group-hover:opacity-100" />
                                    </a>
                                </li>
                            );
                        })}
                    </ul>
                    <div className="mt-2 text-[10px] text-muted-foreground leading-relaxed">
                        Based on currently indexed statutes, regulations, legislative records, and case law from the sources above.
                        Coverage varies — speak with a local attorney to verify any law that affects your situation.
                    </div>
                </>
            )}
        </div>
    );
}
